import type { Lot } from '../../types/lot'
import type { LotCostEvent } from '../../types/production'

// Findings #131 (rehearsal 2): a lot sown into several units ("Sembrar en
// varias unidades") is one arrival lot split into one lot per unit, all sharing
// `lineageRootId`. Its costs (e.g. the eggs' purchase) were typed per unit, and
// "Costo por 1.000 alevines" only covered one unit. A cost for "todo el lote"
// is one amount split by the eggs / fish each unit received
// (`metadata.stockedFishCount`) and saved as one LotCostEvent per unit, linked
// by `metadata.costGroupId` — so cost per kg, reports and exports keep working
// per lot, unchanged.

export const LOT_GROUP_SELECTION_PREFIX = 'group:'

export type LotCostGroupMember = {
  lotId: string
  productionUnitId: string
  /** Eggs / fish the unit received when the lot was sown; null when unknown. */
  count: number | null
}

export type LotCostGroup = {
  rootId: string
  lotKey: string
  members: LotCostGroupMember[]
}

export const lotGroupSelectionValue = (rootId: string): string => `${LOT_GROUP_SELECTION_PREFIX}${rootId}`

export const parseLotGroupSelection = (value: string | null | undefined): string | null => {
  const raw = (value ?? '').trim()
  return raw.startsWith(LOT_GROUP_SELECTION_PREFIX) ? raw.slice(LOT_GROUP_SELECTION_PREFIX.length) || null : null
}

const readStockedCount = (lot: Pick<Lot, 'metadata'>): number | null => {
  const raw = lot.metadata && typeof lot.metadata === 'object' ? (lot.metadata as Record<string, unknown>).stockedFishCount : undefined
  const count = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(count) && count > 0 ? count : null
}

/** Lots of one arrival spread over two or more units (active lots only). */
export const buildLotCostGroups = (lots: ReadonlyArray<Lot>): LotCostGroup[] => {
  const byRoot = new Map<string, LotCostGroup>()
  for (const lot of lots) {
    const rootId = (lot.lineageRootId ?? '').trim()
    const unitId = (lot.productionUnitId ?? '').trim()
    if (!rootId || !unitId || rootId === lot.id || lot.stage !== 'Active') continue
    const group = byRoot.get(rootId) ?? { rootId, lotKey: lot.lotKey, members: [] }
    group.members.push({ lotId: lot.id, productionUnitId: unitId, count: readStockedCount(lot) })
    byRoot.set(rootId, group)
  }
  return Array.from(byRoot.values())
    .filter((group) => group.members.length > 1)
    .sort((a, b) => a.lotKey.localeCompare(b.lotKey))
}

export type LotCostShare = { lotId: string; productionUnitId: string; amountCents: number; count: number | null; percent: number }

/**
 * Splits `totalCents` by each member's count (largest remainder, so the shares
 * add up to the total exactly). When any count is unknown, splits evenly.
 */
export const splitCostByCounts = (totalCents: number, members: ReadonlyArray<LotCostGroupMember>): LotCostShare[] => {
  if (members.length === 0) return []
  const allCounted = members.every((member) => member.count !== null && member.count > 0)
  const weights = members.map((member) => (allCounted ? (member.count as number) : 1))
  const weightTotal = weights.reduce((sum, weight) => sum + weight, 0)
  const exact = weights.map((weight) => (totalCents * weight) / weightTotal)
  const floors = exact.map((value) => Math.floor(value))
  let remainder = totalCents - floors.reduce((sum, value) => sum + value, 0)
  const order = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index)
  for (const { index } of order) {
    if (remainder <= 0) break
    floors[index] += 1
    remainder -= 1
  }
  return members.map((member, index) => ({
    lotId: member.lotId,
    productionUnitId: member.productionUnitId,
    amountCents: floors[index],
    count: member.count,
    percent: (weights[index] / weightTotal) * 100
  }))
}

export const readCostGroupId = (event: Pick<LotCostEvent, 'metadata'>): string | null => {
  const raw = event.metadata && typeof event.metadata === 'object' ? (event.metadata as Record<string, unknown>).costGroupId : undefined
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null
}

export type CostListEntry =
  | { kind: 'single'; event: LotCostEvent }
  | { kind: 'group'; groupId: string; events: LotCostEvent[]; totalCents: number }

/** One list row per cost group (its total), one per ungrouped cost. Keeps the input order. */
export const groupCostEventsForList = (events: ReadonlyArray<LotCostEvent>): CostListEntry[] => {
  const entries: CostListEntry[] = []
  const groupIndex = new Map<string, number>()
  for (const event of events) {
    const groupId = readCostGroupId(event)
    if (!groupId) {
      entries.push({ kind: 'single', event })
      continue
    }
    const existing = groupIndex.get(groupId)
    if (existing === undefined) {
      groupIndex.set(groupId, entries.length)
      entries.push({ kind: 'group', groupId, events: [event], totalCents: event.amountCents })
      continue
    }
    const entry = entries[existing] as Extract<CostListEntry, { kind: 'group' }>
    entry.events.push(event)
    entry.totalCents += event.amountCents
  }
  return entries
}

// Findings #149 (rehearsal 2): the Costos lot list showed `Prueba Peces`,
// `Prueba Peces`, `Prueba Peces (Inactivo)` — unit lots without their unit, and
// the arrival record of "Sembrar en varias unidades" (facility-level, Inactive,
// never holds costs). The list is now one section per lot: its "todo el lote"
// entry (when sown into several units), then one entry per unit lot.

/** The arrival record of "Sembrar en varias unidades": no unit, `metadata.stockedIntoUnits`. */
export const isStockIntoUnitsArrivalRecord = (lot: Pick<Lot, 'productionUnitId' | 'metadata'>): boolean => {
  if ((lot.productionUnitId ?? '').trim()) return false
  const metadata = lot.metadata && typeof lot.metadata === 'object' ? (lot.metadata as Record<string, unknown>) : null
  return metadata?.stockedIntoUnits === true
}

export type CostLotPickerSection = {
  lotKey: string
  /** Present when the lot was sown into several units ("todo el lote"). */
  group?: LotCostGroup
  lots: Lot[]
}

/**
 * Sections for the Costos lot list, sorted by lot key; a section's lots are
 * sorted by unit name. `groups` are the "todo el lote" groups to offer.
 */
export const buildCostLotPickerSections = (
  lots: ReadonlyArray<Lot>,
  groups: ReadonlyArray<LotCostGroup>,
  unitName: (productionUnitId: string) => string
): CostLotPickerSection[] => {
  const groupByLotId = new Map<string, LotCostGroup>()
  for (const group of groups) for (const member of group.members) groupByLotId.set(member.lotId, group)
  const sections = new Map<string, CostLotPickerSection>()
  for (const group of groups) sections.set(`group:${group.rootId}`, { lotKey: group.lotKey, group, lots: [] })
  for (const lot of lots) {
    if (isStockIntoUnitsArrivalRecord(lot)) continue
    const group = groupByLotId.get(lot.id)
    const sectionId = group ? `group:${group.rootId}` : `key:${(lot.lotKey || lot.id).trim()}`
    const section = sections.get(sectionId) ?? { lotKey: lot.lotKey || lot.id, lots: [] }
    section.lots.push(lot)
    sections.set(sectionId, section)
  }
  const unitOf = (lot: Lot) => unitName((lot.productionUnitId ?? '').trim())
  return Array.from(sections.values())
    .map((section) => ({ ...section, lots: [...section.lots].sort((a, b) => unitOf(a).localeCompare(unitOf(b))) }))
    .sort((a, b) => a.lotKey.localeCompare(b.lotKey))
}
